/* eslint-disable react-refresh/only-export-components */

import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2101InitLU } from '../u2101LogicalUnit/U2101';
import type { U3147InitLU, U3147InitLUEmployee, U3147TimeSession } from './U3147Service';
import { u3147SetDisplayTimezone, u3147TodayIsoDate } from './tools/u3147FormatDuration';
import { U2000_ORG_DISPLAY_TIMEZONE } from '../u2000Core/tools/U2000DateTimeHelpers';

export interface U3147ContextType {
  initLU: U3147InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3147TimeSession>;
  employee: U3147InitLUEmployee | null;
  roundingMinutes: number;
  organizationTimezone: string;
  openSessionId: string | null;
  setOpenSessionId: (id: string | null) => void;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  viewEmployeeId: string | null;
  setViewEmployeeId: (id: string | null) => void;
  refreshDayKey: number;
  bumpRefreshDay: () => void;
}

interface U3147ContextProviderProps {
  initLU: U3147InitLU;
  children: ReactNode;
}

const U3147Context = React.createContext<U3147ContextType | undefined>(undefined);

export const U3147ContextProvider: React.FC<U3147ContextProviderProps> = ({ initLU, children }) => {
  const organizationTimezone = initLU.organizationTimezone ?? U2000_ORG_DISPLAY_TIMEZONE;
  u3147SetDisplayTimezone(organizationTimezone);

  const luStateHandlers = useU2000LUStateHandlers({
    initLU: initLU as unknown as U2101InitLU,
  });
  const searchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3147',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const gridStateHandlers = useU2000GridStateHandlers<U3147TimeSession>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const employee = initLU.employee ?? null;
  const [openSessionId, setOpenSessionId] = useState<string | null>(initLU.openSessionId ?? null);
  const [selectedDate, setSelectedDate] = useState<string>(() => u3147TodayIsoDate(organizationTimezone));
  const [viewEmployeeId, setViewEmployeeId] = useState<string | null>(employee?.id ?? null);
  const [refreshDayKey, setRefreshDayKey] = useState(0);

  const bumpRefreshDay = useCallback(() => {
    setRefreshDayKey((k) => k + 1);
  }, []);

  const contextValue = useMemo<U3147ContextType>(
    () => ({
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employee,
      roundingMinutes: initLU.roundingMinutes ?? 1,
      organizationTimezone,
      openSessionId,
      setOpenSessionId,
      selectedDate,
      setSelectedDate,
      viewEmployeeId,
      setViewEmployeeId,
      refreshDayKey,
      bumpRefreshDay,
    }),
    [
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employee,
      openSessionId,
      selectedDate,
      viewEmployeeId,
      refreshDayKey,
      bumpRefreshDay,
    ],
  );

  return <U3147Context.Provider value={contextValue}>{children}</U3147Context.Provider>;
};

export function useU3147Context(): U3147ContextType {
  const context = useContext(U3147Context);
  if (!context) {
    throw new Error('useU3147Context must be used within a U3147ContextProvider');
  }
  return context;
}
